import {useEffect, useMemo, useState} from 'react';

import classnames from 'classnames';
import {Prs} from "store/post-results-services/postResultsServicesApi";
import {toastr} from 'react-redux-toastr';
import {PrsStatus, ROUTES} from "core";
import {generatePath, useNavigate} from 'react-router-dom';

import {Button} from '@britishcouncil/react-solas';

import {ServiceCard} from '../../components/ServiceCard/ServiceCard';
import {PrsCancelModal} from '../../PrsCancelModal';
import {SelectPrsPaymentMethodParams} from "../SelectPrsPaymentMethod";

import {toCancel, toPay, useRequestsList} from './useRequestsList';
import * as S from './RequestsList.style';

const pageSize = 4;

export type RequestsListProps = {
    candidateId: string,
    data: Prs[];
    onChangeHasSelectedMode: (val: boolean) => void;
};

export const RequestsList = ({candidateId, data, onChangeHasSelectedMode}: RequestsListProps) => {
    const {chooseMode, selectedCards, handleOnCheckCard, handleOnchangeChooseMode} = useRequestsList();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [currentPageIndex, setCurrentPageIndex] = useState<number>(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (chooseMode) {
            onChangeHasSelectedMode(true);
        } else {
            onChangeHasSelectedMode(false);
        }
    }, [chooseMode, onChangeHasSelectedMode]);

    const handleToPay = () => {

        const params: SelectPrsPaymentMethodParams = {
            prsIds: selectedCards,
            navigationSource: "list"
        }

        navigate(generatePath(ROUTES.SELECT_PAYMENT_METHOD_POST_RESULTS_SERVICES, {paramsBase64String: btoa(JSON.stringify(params))}));
    }

    const filteredData = useMemo(
        () => {
            if (data) {
                if (chooseMode === 'toCancel' || chooseMode === 'toPay') {
                    return data.filter(p => p.status === PrsStatus.Unpaid)
                }
                return data;
            }
            return []
        }, [data, chooseMode]
    );

    const noDataToChange = !data.some(p => p.status === PrsStatus.Unpaid)

    useEffect(() => {
        setCurrentPageIndex(0);
    }, [filteredData]);

    return (
        <>
            {!chooseMode && filteredData.length > 0 && (
                <S.ContentRow>
                    <S.CancelButton className={classnames({'btn-disable': noDataToChange})}
                                    onClick={() => handleOnchangeChooseMode(toCancel)}
                                    disabled={noDataToChange}>
                        Choose to cancel
                    </S.CancelButton>
                    <Button className={classnames({'btn-disable': noDataToChange})}
                            onClick={() => handleOnchangeChooseMode(toPay)} 
                            disabled={noDataToChange}>Choose to pay
                    </Button>
                </S.ContentRow>
            )}

            {chooseMode === toPay && <S.SelectedModeText>Choose records to be paid</S.SelectedModeText>}
            {chooseMode === toCancel && <S.SelectedModeText>Select the records to be cancelled.</S.SelectedModeText>}

            <div>
                {filteredData
                    .slice(currentPageIndex * pageSize, currentPageIndex * pageSize + pageSize).map((card) => (
                        <ServiceCard
                            key={card.id}
                            data={card}
                            displayCheckbox={!!chooseMode}
                            deadlinePassed={card.deadlineHasPassed}
                            checkboxValue={selectedCards.includes(card.id)}
                            onChangeCheckbox={(checked) => handleOnCheckCard(checked, card.id)}
                            items={card.prsComponents}
                        />
                    ))}
            </div>

            <S.StyledPagination
                pageCount={Math.ceil(filteredData.length / pageSize)}
                pageIndex={currentPageIndex}
                onPageIndexChange={(pageIndex) => {
                    setCurrentPageIndex(pageIndex);
                }}/>

            {!!chooseMode && (
                <S.FooterButtonsContainer>
                    <S.DiscardButton onClick={() => handleOnchangeChooseMode(undefined)}>Discard</S.DiscardButton>
                    {chooseMode === toPay && (
                        <S.ConfirmButton
                            className={classnames({
                                'btn-disable': !selectedCards.length,
                            })}
                            disabled={!selectedCards.length}
                            onClick={handleToPay}
                        >
                            Choose payment method
                        </S.ConfirmButton>
                    )}
                    {chooseMode === toCancel && (
                        <S.CancelButton
                            className={classnames({
                                'btn-disable': !selectedCards.length,
                            })}
                            disabled={!selectedCards.length}
                            onClick={() => setShowCancelModal(true)}>
                            Choose to cancel
                        </S.CancelButton>
                    )}
                </S.FooterButtonsContainer>
            )}

            {showCancelModal && <PrsCancelModal
                candidateId={candidateId}
                onSubmitted={() => {
                    toastr.success('Success', 'Cancellation successful');
                    handleOnchangeChooseMode(undefined)
                }}
                onExit={() => setShowCancelModal(false)}
                prsesIds={selectedCards}
            />}
        </>
    );
};
